import React, {useCallback, useRef} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {useIsAnalysisLayoutCreationEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import confirmAndDeleteAnalysisView from 'analysis-tool/confirmAndDeleteAnalysisView';
import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {isLayoutView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExperimentOptimizationFeedbackToolbar from 'machine-learning/experiment-optimization/ExperimentOptimizationFeedbackToolbar';
import {pushToast} from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {assertPipelineStepIsStepType, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import {confirm} from 'util/Modals';
import {withLoadingScreen} from 'util/Promises';
import {assertNever, JSONValue} from 'util/Types';

import {AnalysisLayoutStatus} from './analysis-adapter/AnalysisAdapterTypes';
import getAnalysisLayoutStatusInfo from './analysis-adapter/getAnalysisLayoutStatusInfo';
import useHandleAnalysisLayoutEvents from './analysis-adapter/useHandleAnalysisLayoutEvents';
import DataLayout from './DataLayout';
import type {
  AddBlockEvent,
  ConfigFormFieldEvent,
  DataLayout as DataLayoutType,
  LayoutEvent,
  RemoveBlockEvent,
} from './DataLayoutTypes';
import FailedLayout from './FailedLayout';
import LayoutSettingsModal from './layout-settings/LayoutSettingsModal';
import LayoutViewPicker from './LayoutViewPicker';
import {
  buildAddViewEdge,
  convertEdgeToUpsert,
  filterEdgesToRemainingRefs,
  getInputDataNameForView,
  getNextInputDataIndex,
  normalizeEdgeIndices,
  replaceBlockInConfig,
} from './LayoutViewUtils';
import RunningLayout from './RunningLayout';
import {isLockedLayout} from './utils/LayoutCreationUtils';
import {getLayoutDimensions, resizeLayoutGrid} from './utils/LayoutSettingsUtils';

export type AnalysisDataLayoutAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_LAYOUT>;

/**
 * Adapter between an analysis layout step and the actual data layout component.
 *
 * This component is responsible for handling analysis-specific concerns, such
 * as the status of the layout step.
 */
export default function AnalysisDataLayoutAdapter(props: AnalysisDataLayoutAdapterProps): JSX.Element {
  const layoutStep = getStepFromAnalysis(props.analysis, props.step.id);
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);

  const statusInfo = getAnalysisLayoutStatusInfo(props.analysis, layoutStep);

  if (statusInfo.status === AnalysisLayoutStatus.RUNNING) {
    return <RunningLayout message={statusInfo.message} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.FAILED) {
    return <FailedLayout message={statusInfo.message} reason={statusInfo.reason} />;
  } else if (statusInfo.status === AnalysisLayoutStatus.READY) {
    return <ReadyAnalysisDataLayoutAdapter {...props} layoutStep={layoutStep} />;
  } else {
    throw new Error(`Unknown layout status: ${assertNever(statusInfo)}`);
  }
}

async function openLayoutSettingsAndSave(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  layout: DataLayoutType
): Promise<void> {
  const dimensions = getLayoutDimensions(layout.sections);
  const result = await runModal(LayoutSettingsModal, {
    currentName: layout.name,
    currentRows: dimensions.rows,
    currentColumns: dimensions.columns,
  });
  if (result == null) {
    return;
  }
  const backendSections = layoutStep.config!.sections as unknown as DataLayoutType['sections'];
  const {config, displacedBlockCount} = resizeLayoutGrid(
    backendSections,
    result.name,
    result.rows,
    result.columns
  );
  if (displacedBlockCount > 0) {
    const confirmed = await confirm(
      'Resize layout',
      `Resizing the grid will remove ${displacedBlockCount} data ${displacedBlockCount === 1 ? 'view' : 'views'} from the layout.`,
      ButtonStyle.NEGATIVE,
      {acceptText: 'Continue', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
    );
    if (!confirmed) {
      return;
    }
  }
  const updatedConfig = {
    ...(layoutStep.config as unknown as Record<string, JSONValue>),
    ...config,
  } as unknown as Record<string, JSONValue>;

  // When blocks are displaced, remove edges that no longer correspond to any DATA block.
  const stepUpdate: {
    stepId: string;
    config: Record<string, JSONValue>;
    incomingEdges?: Array<ReturnType<typeof convertEdgeToUpsert>>;
  } = {
    stepId: layoutStep.id,
    config: updatedConfig,
  };
  if (displacedBlockCount > 0) {
    stepUpdate.incomingEdges = filterEdgesToRemainingRefs(layoutStep.incomingEdges, config.sections);
  }

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [stepUpdate],
  });
}

async function duplicateLayout(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>
): Promise<void> {
  const layoutView = getLayoutView(analysis, layoutStep);
  if (layoutView == null) {
    throw new Error('Could not find view for layout step');
  }

  const stepIds = layoutView.lineage.map((node) => node.step.id);
  await AnalysisActions.copyAnalysisSteps(analysis.id, stepIds);
}

function getLayoutView(
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>
): PipelineViewGraphQL | undefined {
  return analysis.views.find(
    (view) => isLayoutView(view) && view.lineage[view.lineage.length - 1].step.id === layoutStep.id
  );
}

function getLayoutOverflowMenuOptions({
  analysis,
  analysisClientStateId,
  layoutStep,
  layout,
  isLayoutCreationEnabled,
}: {
  analysis: AnalysisDataLayoutAdapterProps['analysis'];
  analysisClientStateId: string;
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>;
  layout: DataLayoutType;
  isLayoutCreationEnabled: boolean;
}): Array<OverflowMenuOption> {
  if (isAnalysisReadOnly(analysis) || isLockedLayout(layoutStep.config!)) {
    return [];
  }

  const options: Array<OverflowMenuOption> = [];

  if (isLayoutCreationEnabled) {
    options.push({
      icon: 'gear',
      contents: <span>Settings</span>,
      onSelect: () => openLayoutSettingsAndSave(analysis, layoutStep, layout),
    });

    const {canEdit: canDuplicate, message: duplicateMessage} = checkCanModifyStep(
      analysis,
      layoutStep.id,
      'view',
      'modifyStep'
    );
    options.push({
      icon: 'copy',
      contents: <span>Duplicate</span>,
      isDisabled: !canDuplicate,
      tooltipTitle: !canDuplicate ? duplicateMessage : undefined,
      onSelect: () => withLoadingScreen(duplicateLayout(analysis, layoutStep)),
    });
  }

  const {canEdit, message} = checkCanModifyStep(analysis, layoutStep.id, 'view', 'delete');

  options.push({
    icon: 'delete',
    contents: <span>Delete view</span>,
    isDisabled: !canEdit,
    tooltipTitle: !canEdit ? message : undefined,
    onSelect: () =>
      confirmAndDeleteAnalysisView({
        analysis,
        stepId: layoutStep.id,
        stepType: PipelineStepType.LAYOUT,
        analysisClientStateId,
        typeName: 'view',
      }),
  });

  return options;
}

async function handleAddBlock(
  event: AddBlockEvent,
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>
): Promise<void> {
  const selectedView = await runModal(LayoutViewPicker, {analysis});
  if (selectedView == null) {
    return;
  }

  const inputDataName = getInputDataNameForView(selectedView);
  const newEdge = buildAddViewEdge(selectedView, inputDataName, 0);
  if (newEdge == null) {
    return;
  }
  const existingEdges = layoutStep.incomingEdges.map((edge) => convertEdgeToUpsert(edge));

  // If the view already has an edge into the layout, reuse its index for the new DATA block
  // instead of creating a duplicate edge (which would cause a SQLAlchemy identity map conflict).
  const existingEdge = existingEdges.find(
    (edge) =>
      edge.fromStepId === newEdge.fromStepId &&
      edge.fromStepDataName === newEdge.fromStepDataName &&
      edge.fromStepDataIndex === newEdge.fromStepDataIndex
  );

  let allEdges: typeof existingEdges;
  let inputDataIndex: number;
  if (existingEdge != null) {
    allEdges = existingEdges;
    inputDataIndex = existingEdge.toStepDataIndex!;
  } else {
    inputDataIndex = getNextInputDataIndex(layoutStep, inputDataName);
    const edgeWithIndex = {...newEdge, toStepDataIndex: inputDataIndex};
    allEdges = normalizeEdgeIndices([...existingEdges, edgeWithIndex]);
  }

  const newConfig = replaceBlockInConfig(layoutStep.config!, event.layoutPath, {
    type: 'DATA',
    inputReference: {inputDataName, inputDataIndex},
  });

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [
      {
        stepId: layoutStep.id,
        incomingEdges: allEdges,
        config: newConfig as unknown as Record<string, JSONValue>,
        rerunAfterUpdate: true,
      },
    ],
  });
}

async function handleRemoveBlock(
  event: RemoveBlockEvent,
  analysis: AnalysisDataLayoutAdapterProps['analysis'],
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>
): Promise<void> {
  const updatedConfig = replaceBlockInConfig(layoutStep.config!, event.layoutPath, {type: 'PLACEHOLDER'});
  const remainingEdges = filterEdgesToRemainingRefs(layoutStep.incomingEdges, updatedConfig.sections);

  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: [],
    stepsToUpdate: [
      {
        stepId: layoutStep.id,
        incomingEdges: remainingEdges,
        config: updatedConfig as unknown as Record<string, JSONValue>,
        rerunAfterUpdate: true,
      },
    ],
  });
}

function ReadyAnalysisDataLayoutAdapter({
  analysis,
  analysisClientStateId,
  clientStateId,
  layoutStep,
}: AnalysisDataLayoutAdapterProps & {layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>}): JSX.Element {
  const {handleOnEvents, layout} = useHandleAnalysisLayoutEvents({
    analysis,
    clientStateId,
    layoutStep,
  });
  const isLayoutCreationEnabled = useIsAnalysisLayoutCreationEnabled();

  const overflowMenuOptions = getLayoutOverflowMenuOptions({
    analysis,
    analysisClientStateId,
    layoutStep,
    layout,
    isLayoutCreationEnabled,
  });

  const canEditLayout = !isLockedLayout(layoutStep.config!) && !isAnalysisReadOnly(analysis);
  // Prevent concurrent add-block calls. getNextInputDataIndex uses raw datas length,
  // which only updates after the layout step re-runs. Without this lock, two rapid adds
  // would read the same length and produce duplicate toStepDataIndex values.
  const isAddingBlockRef = useRef(false);
  // Prevent concurrent remove-block calls. Each remove reads from layoutStep.config,
  // which only updates after the step re-runs. Without this lock, two rapid removes
  // would compute from stale config and the later one could overwrite the earlier.
  const isRemovingBlockRef = useRef(false);

  const handleAllEvents = useCallback(
    (events: Array<LayoutEvent>) => {
      const addBlockEvents = events.filter((e): e is AddBlockEvent => e.type === 'ADD_BLOCK');
      const removeBlockEvents = events.filter((e): e is RemoveBlockEvent => e.type === 'REMOVE_BLOCK');
      const configEvents = events.filter((e): e is ConfigFormFieldEvent => e.type === 'CONFIG_FORM_FIELD');

      if (configEvents.length > 0) {
        handleOnEvents(configEvents);
      }

      const nextAddBlock = addBlockEvents[0];
      if (nextAddBlock != null && !isAddingBlockRef.current) {
        isAddingBlockRef.current = true;
        handleAddBlock(nextAddBlock, analysis, layoutStep)
          .catch(() => {
            pushToast({
              type: ToastType.DANGER,
              message: 'Something went wrong while adding the view. Please try again.',
            });
          })
          .finally(() => {
            isAddingBlockRef.current = false;
          });
      }

      for (const event of removeBlockEvents) {
        if (isRemovingBlockRef.current) {
          continue;
        }
        isRemovingBlockRef.current = true;
        handleRemoveBlock(event, analysis, layoutStep)
          .catch(() => {
            pushToast({
              type: ToastType.DANGER,
              message: 'Something went wrong while removing the view. Please try again.',
            });
          })
          .finally(() => {
            isRemovingBlockRef.current = false;
          });
      }
    },
    [analysis, handleOnEvents, layoutStep]
  );

  // TODO BNCH-146595: Use step type ancestry or a dedicated config field instead of matching on layout name.
  const isEOLayout = layout.name === 'Model details' || layout.name === 'Recommendations';

  return (
    <DataLayout
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      additionalTitleComponents={
        isEOLayout ? (
          <ExperimentOptimizationFeedbackToolbar
            clientStateId={clientStateId}
            feedbackContext={{analysisId: analysis.id, layoutStepId: layoutStep.id}}
          />
        ) : undefined
      }
      clientId={layoutStep.id}
      isEditable={canEditLayout}
      layout={layout}
      onEvents={handleAllEvents}
      overflowMenuOptions={overflowMenuOptions}
    />
  );
}
